import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ModalComponentWrapper,
  WalletCard,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { type TransformedLimitsData } from '../model';
import { LimitProgress } from './LimitProgress';
import { formatReceivedAmount } from '@/shared/lib';

type Props = {
  onBack: () => void;
  walletId: string;
  limitsMap: TransformedLimitsData;
};

const classes = tv({
  slots: {
    content: 'flex flex-col gap-[1.6rem]',
    card: '',
    header: 'flex items-center gap-[1.5rem] text-primary',
    icon: 'flex items-center justify-center size-[2.4rem]',
    title: 'headline4',
    body: 'flex flex-col gap-[0.4rem]',
    row: 'flex justify-between items-center',
    meta: 'text-[1.2rem] text-secondary leading-[1.2rem]',
    countGroup: 'flex items-baseline gap-[0.5rem]',
    countValue: 'text-[1.2rem] headline4',
  },
});

export function LimitsModal({ onBack, walletId, limitsMap }: Props) {
  const { t } = useTranslation();

  const { periodRange, baseAsset, limits } = limitsMap;

  const c = classes();

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.limits.title')}
      onBack={onBack}
      headerClassName="text-secondary-bg bg-secondary-bg/50">
      <div className={c.content()}>
        <WalletCard
          walletId={walletId}
          label={t('personalAccount.userProfile.limits.walletCardLabelText')}
        />

        {limits.map(({ code, name, icon, used, limit }) => {
          return (
            <Card key={code} className={c.card()}>
              <CardHeader className={c.header()}>
                <div className={c.icon()}>
                  <img src={icon} alt={name} />
                </div>
                <CardTitle className={c.title()}>{name}</CardTitle>
              </CardHeader>

              <CardContent className={c.body()}>
                <div className={c.row()}>
                  <span className={c.meta()}>
                    {t('personalAccount.userProfile.limits.used')}:
                  </span>
                  <span className={c.meta()}>
                    {t('personalAccount.userProfile.limits.yourLimit')}:
                  </span>
                </div>

                <div className={c.row()}>
                  <div className={c.countGroup()}>
                    <span className={c.countValue()}>
                      {formatReceivedAmount(used)}
                    </span>
                    <span className={c.meta()}>{baseAsset}</span>
                  </div>
                  <div className={c.countGroup()}>
                    <span className={c.countValue()}>
                      {formatReceivedAmount(limit)}
                    </span>
                    <span className={c.meta()}>{baseAsset}</span>
                  </div>
                </div>

                <LimitProgress used={used} limit={limit} />

                <div className={c.row()}>
                  {periodRange.isMonth && (
                    <span className={c.meta()}>{periodRange.from}</span>
                  )}
                  <span className={c.meta()}>{periodRange.to}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </ModalComponentWrapper>
  );
}
